import { levelForTotalXp, totalXpAtLevel, xpToNextLevel } from "./progression.js";

const RECOVERY_ANCHORS: ReadonlyArray<readonly [number, number]> = [
  [1, 5 * 60],
  [10, 10 * 60],
  [20, 15 * 60],
  [30, 20 * 60],
  [40, 30 * 60],
  [50, 45 * 60],
  [60, 60 * 60],
  [80, 90 * 60],
  [100, 150 * 60],
  [150, 240 * 60],
  [200, 360 * 60],
  [250, 540 * 60],
];

export interface HospitalLevelDefinition {
  readonly level: number;
  readonly beds: number;
  /** Portion of a ninja's base fatal-recovery time used by this building level. */
  readonly treatmentTimeMultiplier: number;
}

/** Canonical Hospital effects, kept together so level data cannot drift across parallel arrays. */
export const HOSPITAL_LEVELS = [
  { level: 1, beds: 1, treatmentTimeMultiplier: 1 },
  { level: 2, beds: 1, treatmentTimeMultiplier: 0.92 },
  { level: 3, beds: 2, treatmentTimeMultiplier: 0.85 },
  { level: 4, beds: 2, treatmentTimeMultiplier: 0.78 },
  { level: 5, beds: 3, treatmentTimeMultiplier: 0.7 },
  { level: 6, beds: 3, treatmentTimeMultiplier: 0.63 },
  { level: 7, beds: 4, treatmentTimeMultiplier: 0.57 },
  { level: 8, beds: 4, treatmentTimeMultiplier: 0.5 },
  { level: 9, beds: 5, treatmentTimeMultiplier: 0.45 },
  { level: 10, beds: 5, treatmentTimeMultiplier: 0.4 },
] as const satisfies readonly HospitalLevelDefinition[];

export const HOSPITAL_MAX_LEVEL = HOSPITAL_LEVELS.at(-1)!.level;

/** Temporary MVP bootstrap: start maxed to exercise every configured Hospital benefit. */
export const HOSPITAL_MVP_INITIAL_LEVEL = HOSPITAL_MAX_LEVEL;
export const HOSPITAL_MAX_TREATMENT_MS = 12 * 60 * 60 * 1000;

export function hospitalLevelDefinition(level: number): HospitalLevelDefinition {
  if (!Number.isInteger(level) || level < 1 || level > HOSPITAL_MAX_LEVEL)
    throw new RangeError(`hospitalLevel must be an integer from 1 to ${HOSPITAL_MAX_LEVEL}`);
  return HOSPITAL_LEVELS[level - 1]!;
}

/** Fatality removes 10% of the current level's XP requirement, capped at in-level progress. */
export function fatalityXpLoss(totalXp: number): number {
  if (!Number.isFinite(totalXp) || totalXp < 0)
    throw new RangeError("totalXp must be non-negative");
  const level = levelForTotalXp(totalXp);
  const progressWithinLevel = totalXp - totalXpAtLevel(level);
  const basePenalty = Math.floor(xpToNextLevel(level) * 0.1);
  return Math.min(progressWithinLevel, basePenalty);
}

/** Interpolates the canonical level/time anchors; above level 250 growth is sublinear. */
export function hospitalBaseRecoveryMs(level: number): number {
  if (!Number.isInteger(level) || level < 1)
    throw new RangeError("level must be a positive integer");
  const maxAnchor = RECOVERY_ANCHORS.at(-1)!;
  let seconds: number;
  if (level <= maxAnchor[0]) {
    const upperIndex = RECOVERY_ANCHORS.findIndex(([anchorLevel]) => anchorLevel >= level);
    if (upperIndex <= 0) seconds = RECOVERY_ANCHORS[0]![1];
    else {
      const [upperLevel, upperSeconds] = RECOVERY_ANCHORS[upperIndex]!;
      const [lowerLevel, lowerSeconds] = RECOVERY_ANCHORS[upperIndex - 1]!;
      const progress = (level - lowerLevel) / (upperLevel - lowerLevel);
      seconds = lowerSeconds + (upperSeconds - lowerSeconds) * progress;
    }
  } else {
    // The design specifies sublinear growth after level 250 but leaves the curve
    // open. Square-root scaling is monotonic, sublinear, and bounded by the cap.
    seconds = maxAnchor[1] * Math.sqrt(level / maxAnchor[0]);
  }
  return Math.min(HOSPITAL_MAX_TREATMENT_MS, Math.round(seconds * 1000));
}

export function hospitalTreatmentMs(level: number, hospitalLevel = 1): number {
  return Math.min(
    HOSPITAL_MAX_TREATMENT_MS,
    Math.round(
      hospitalBaseRecoveryMs(level) *
        hospitalLevelDefinition(hospitalLevel).treatmentTimeMultiplier,
    ),
  );
}

export function hospitalBeds(hospitalLevel: number): number {
  return hospitalLevelDefinition(hospitalLevel).beds;
}
